/**
 * Pasang CSS produk ke Shadow Root widget.
 *
 * Kenapa Shadow DOM: Preflight / `* { font-family }` ui-kit tidak boleh
 * merusak halaman host (Hub, ERP, MIS).
 * Kenapa inline ke JS (bukan fetch `mjl-ui-kit.min.css`): tiap produk mandiri
 * seperti Google Maps — load My Apps tidak mengunduh CSS Spotlight.
 *
 * `@import` di-strip (constructable stylesheet mengabaikannya); URL Google Fonts
 * diangkat ke `<link>` di `document.head` sekali saja.
 */

const sheets = new Map<string, CSSStyleSheet>();

/** Gabung hasil `?inline` Vite (string CSS) jadi satu stylesheet. */
export function joinCss(...mods: unknown[]): string {
  return mods.map((mod) => (typeof mod === 'string' ? mod : '')).join('\n');
}

/** `:root` / `[data-project]` ui-kit → berlaku di dalam `:host` shadow. */
function scopeToShadow(css: string): string {
  return css
    .replace(/:root\b/g, ':host,:root')
    .replace(/(?<!:host\()\[data-project=([^\]]+)\]/g, ':host([data-project=$1])')
    .replace(/(?<!:host\()\[data-platform=([^\]]+)\]/g, ':host([data-platform=$1])');
}

function extractImports(css: string): { hrefs: string[]; body: string } {
  const hrefs: string[] = [];
  const body = css.replace(/@import\s*(?:url\()?["']([^"']+)["']\)?\s*;/g, (_, href: string) => {
    hrefs.push(href);
    return '';
  });
  return { hrefs, body };
}

function ensureFontLinks(hrefs: string[]): void {
  for (const href of hrefs) {
    if (document.querySelector(`link[data-mjl-font="${CSS.escape(href)}"]`)) continue;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.dataset.mjlFont = href;
    document.head.appendChild(link);
  }
}

/** Satu CSSStyleSheet per string CSS (My Apps ≠ Spotlight). */
function getSheet(cssText: string): CSSStyleSheet {
  const cached = sheets.get(cssText);
  if (cached) return cached;

  const { hrefs, body } = extractImports(cssText);
  ensureFontLinks(hrefs);

  const sheet = new CSSStyleSheet();
  sheet.replaceSync(scopeToShadow(body));
  sheets.set(cssText, sheet);
  return sheet;
}

/**
 * @param host custom element yang sudah punya shadowRoot (default Lit)
 * @param cssText output `widgetCss` dari `src/styles/css.ts` package itu
 */
export async function attachUiKitShadow(host: HTMLElement, cssText: string): Promise<void> {
  const project = document.documentElement.getAttribute('data-project');
  if (project) host.setAttribute('data-project', project);
  const platform = document.documentElement.getAttribute('data-platform');
  if (platform) host.setAttribute('data-platform', platform);

  const root = host.shadowRoot;
  if (!root || !cssText) return;

  const sheet = getSheet(cssText);
  if (!root.adoptedStyleSheets.includes(sheet)) {
    root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
  }
}
